import { useCallback, useEffect, useRef, useState } from "react";

const REVEAL_WIDTH = 64; // px – width of the delete action behind the row
const SNAP_THRESHOLD = 30; // px – minimum swipe distance to snap open
const DIRECTION_LOCK_THRESHOLD = 6; // px – movement before locking to horizontal or vertical

type SwipeState =
  | { phase: "idle" }
  | { phase: "pending"; startX: number; startY: number }
  | { phase: "swiping"; startX: number; currentOffset: number };

interface SwipeCallbacks {
  onOpen?: () => void;
  onClose?: () => void;
}

export const useSwipeToReveal = (
  externalOpen: boolean,
  callbacks?: SwipeCallbacks,
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const swipeState = useRef<SwipeState>({ phase: "idle" });
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  // Snap to the externally-controlled open/closed state
  useEffect(() => {
    setTransitioning(true);
    setOffset(externalOpen ? -REVEAL_WIDTH : 0);
  }, [externalOpen]);

  // Close when tapping outside the revealed container
  useEffect(() => {
    if (offset !== -REVEAL_WIDTH) return;

    const handleOutsideTouch = (e: TouchEvent | MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setTransitioning(true);
        setOffset(0);
        callbacksRef.current?.onClose?.();
      }
    };

    document.addEventListener("touchstart", handleOutsideTouch, true);
    document.addEventListener("mousedown", handleOutsideTouch, true);
    return () => {
      document.removeEventListener("touchstart", handleOutsideTouch, true);
      document.removeEventListener("mousedown", handleOutsideTouch, true);
    };
  }, [offset]);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    swipeState.current = { phase: "pending", startX: touch.clientX, startY: touch.clientY };
    setTransitioning(false);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    const state = swipeState.current;
    const touch = e.touches[0];

    if (state.phase === "pending") {
      const deltaX = Math.abs(touch.clientX - state.startX);
      const deltaY = Math.abs(touch.clientY - state.startY);

      if (deltaY > DIRECTION_LOCK_THRESHOLD) {
        swipeState.current = { phase: "idle" };
        return;
      }
      if (deltaX > DIRECTION_LOCK_THRESHOLD) {
        swipeState.current = { phase: "swiping", startX: state.startX, currentOffset: offset };
      }
      return;
    }

    if (state.phase === "swiping") {
      const delta = touch.clientX - state.startX;
      const raw = state.currentOffset + delta;
      const clamped = Math.max(-REVEAL_WIDTH, Math.min(0, raw));
      setOffset(clamped);
    }
  }, [offset]);

  const onTouchEnd = useCallback(() => {
    const state = swipeState.current;
    swipeState.current = { phase: "idle" };

    if (state.phase !== "swiping") return;

    setTransitioning(true);
    if (offset < -SNAP_THRESHOLD) {
      setOffset(-REVEAL_WIDTH);
      callbacksRef.current?.onOpen?.();
    } else {
      setOffset(0);
      callbacksRef.current?.onClose?.();
    }
  }, [offset]);

  const reset = useCallback(() => {
    setTransitioning(true);
    setOffset(0);
  }, []);

  const contentStyle: React.CSSProperties = {
    transform: `translateX(${offset}px)`,
    transition: transitioning ? "transform 200ms ease-out" : "none",
  };

  return {
    containerRef,
    contentStyle,
    offset,
    reset,
    handlers: { onTouchStart, onTouchMove, onTouchEnd },
  };
};
